import React, { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import ConfirmPopup from '@components/ConfirmPopup'
import { EVENT_ACTIVITY_KEYS } from '@modules/activity/activityKeys'
import { useTrackUserActivity } from '@modules/activity/useTrackUserActivity'
import shortlistApi from '@modules/shortlist/api'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import customTagsApi from '../../api'
import { CUSTOM_TAG_AVAILABLE_COLORS } from '../../constants'
import useCustomTagData from '../../hooks/useCustomTagsData'
import { CustomTag } from '../../types'
import { AddNewStatusButton, NoMoreStatusesLeftMessage, TagsWrapper } from '../index.styles'
import CustomTagRow from './CustomTagRow'

export interface EditCustomTagsPopoverProps {
  isOpen: boolean
  onClose: () => void
  activeShortlistId: number
}

const EditCustomTagsPopover = ({ isOpen, onClose, activeShortlistId }: EditCustomTagsPopoverProps) => {
  const { tags: initialTags } = useCustomTagData()

  const [tags, setTags] = useState<CustomTag[]>([])

  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const [tagToDelete, setTagToDelete] = useState<CustomTag | undefined>()
  const [tagToEdit, setTagToEdit] = useState<CustomTag | undefined>()
  const [unsavedChangesExist, setUnsavedChangesExist] = useState(false)
  const [validationErrorKeys, setValidationErrorKeys] = useState<{ title: string; text: string } | undefined>(undefined)
  const [showUnsavedChangesConfirmPopup, setShowUnsavedChangesConfirmPopup] = useState(false)

  const { trackActivity } = useTrackUserActivity()

  useEffect(() => {
    setTags(initialTags.sort((tagA, tagB) => tagA.order - tagB.order))
  }, [initialTags])

  const { mutate: createTagMutation } = useMutation({
    mutationFn: customTagsApi.createCustomTag.fn,
    onSuccess: (data, createdTag) => {
      const indexToUpdate = tags.findIndex(tag => tag === createdTag)
      tags[indexToUpdate] = data
      const newTags = [...tags]
      setTags(newTags)

      if (newTags.some(tag => !tag.id)) {
        const firstTagToCreate = newTags.find(tag => !tag.id)
        createTagMutation(firstTagToCreate as CustomTag)
      } else {
        updateTagsMutation(newTags)
      }

      queryClient.setQueryData([customTagsApi.fetchCustomTags.key], () => newTags)
    },
  })

  const { mutate: updateTagsMutation } = useMutation({
    mutationFn: customTagsApi.updateCustomTag.fn,
    onSuccess: () => {
      onClose()
      queryClient.invalidateQueries({ queryKey: [customTagsApi.fetchCustomTags.key] })
      queryClient.invalidateQueries({ queryKey: [shortlistApi.getShortlistPageData.key, activeShortlistId] })
    },
  })

  const { mutate: deleteTagMutation } = useMutation({
    mutationFn: customTagsApi.deleteCustomTag.fn,
    onSuccess: () => {
      const newTags = tags.filter(tag => tag !== tagToDelete)
      setTags(newTags)
      setTagToDelete(undefined)
      queryClient.setQueryData([customTagsApi.fetchCustomTags.key], () => newTags)
    },
  })

  const createTag = useCallback(() => {
    const nextColor = CUSTOM_TAG_AVAILABLE_COLORS.find(color => !tags.some(tag => tag.color === color))
    if (nextColor) {
      const newTag = {
        color: nextColor,
        name: '',
        order: tags.length,
      }
      const newTags = [...tags, newTag]
      setTags(newTags)
      setTagToEdit(newTag)
      trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.STATUS.ADD, { newNumberOfOptions: newTags.length })

      setUnsavedChangesExist(true)
    }
  }, [tags, trackActivity])

  const handleTagUpdate = useCallback(
    (tagToUpdate: CustomTag, { color, name }: { color?: string; name?: string }) => {
      const tagIndexToUpdate = tags.findIndex(tag => tag === tagToUpdate)
      const newTag = {
        ...tagToUpdate,
        color: color || tagToUpdate.color,
        name: name === undefined ? tagToUpdate.name : name.substring(0, 20),
      }

      setTags(existingTags => existingTags.map((i, idx) => (idx === tagIndexToUpdate ? newTag : i)))
      setTagToEdit(newTag)
      setUnsavedChangesExist(true)
      trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.STATUS.EDIT)
    },
    [tags, trackActivity],
  )

  const handleConfirm = useCallback(() => {
    const emptyNameExist = tags.some(tag => !tag.name.trim())
    if (emptyNameExist) {
      setValidationErrorKeys({
        title: translationKeys.CUSTOM_LIST_EMPTY_CUSTOM_STATUS_NAME_ERROR_TITLE,
        text: translationKeys.CUSTOM_LIST_EMPTY_CUSTOM_STATUS_NAME_ERROR_TEXT,
      })
      return
    }

    const duplicateNameExists = tags.some(tag =>
      tags.some(otherTag => tag !== otherTag && tag.name.toLowerCase().trim() === otherTag.name.toLowerCase().trim()),
    )
    if (duplicateNameExists) {
      setValidationErrorKeys({
        title: translationKeys.CUSTOM_LIST_DUPLICATE_CUSTOM_STATUS_NAME_ERROR_TITLE,
        text: translationKeys.CUSTOM_LIST_DUPLICATE_CUSTOM_STATUS_NAME_ERROR_TEXT,
      })
      return
    }

    const firstTagToCreate = tags.find(tag => !tag.id)
    if (firstTagToCreate) {
      createTagMutation(firstTagToCreate as CustomTag)
      return
    }

    updateTagsMutation(tags)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags])

  const handleTagDelete = useCallback(
    (deletedTag: CustomTag) => {
      if (deletedTag.id) {
        deleteTagMutation(deletedTag.id)
      } else {
        const newTags = tags.filter(tag => tag !== deletedTag)
        setTags(newTags)
        setTagToDelete(undefined)
        trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.STATUS.DELETE, { newNumberOfOptions: newTags.length })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tags],
  )

  const handleOrderChange = useCallback(
    (tagIndexToMove: number, newOrder: number) => {
      let newTags = [...tags]
      const tagToMove = newTags.splice(tagIndexToMove, 1)[0]
      tagToMove.order = newOrder

      newTags = [...newTags.slice(0, newOrder), tagToMove, ...newTags.slice(newOrder)]

      newTags = newTags.map((tag, index) => ({ ...tag, order: index }))

      setTags(newTags)
      setUnsavedChangesExist(true)
      trackActivity(EVENT_ACTIVITY_KEYS.CUSTOM_LIST.STATUS.MOVE)
    },
    [tags, trackActivity],
  )

  const handleCancelWithConfirm = () => {
    if (unsavedChangesExist) {
      setShowUnsavedChangesConfirmPopup(true)
    } else {
      onClose()
    }
  }

  return (
    <ConfirmPopup
      id="custom-tags-popup"
      data-testid="custom-tags-popup"
      title={t(translationKeys.CUSTOM_LIST_EDIT_CUSTOM_STATUS)}
      text=""
      cancelBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CANCEL_BTN)}
      confirmBtnText={t(translationKeys.CUSTOM_LIST_POPUP_CONFIRM_BTN)}
      onCancel={handleCancelWithConfirm}
      onBackdropClick={handleCancelWithConfirm}
      onConfirm={handleConfirm}
      confirmDisabled={!unsavedChangesExist}
      isOpen={isOpen}
      width={260}
    >
      <TagsWrapper>
        {tags.map(tag => (
          <CustomTagRow
            key={tag.id}
            tag={tag}
            existingTags={tags}
            onOrderChange={(tagIndex, newOrder) => handleOrderChange(tagIndex, newOrder)}
            onDelete={() => setTagToDelete(tag)}
            onTagUpdate={newValues => handleTagUpdate(tag, newValues)}
            shouldEdit={tagToEdit === tag}
          />
        ))}
        {tags.length < CUSTOM_TAG_AVAILABLE_COLORS.length ? (
          <AddNewStatusButton variant="outlined" onClick={() => createTag()}>
            <strong style={{ fontSize: '20px' }}>+</strong>
            {t(translationKeys.CUSTOM_LIST_ADD_NEW_STATUS_BTN)}
          </AddNewStatusButton>
        ) : (
          <NoMoreStatusesLeftMessage>{t(translationKeys.CUSTOM_LIST_NO_MORE_STATUSES)}</NoMoreStatusesLeftMessage>
        )}
      </TagsWrapper>

      {tagToDelete && (
        <ConfirmPopup
          id="delete-tag-confirmation-popup"
          isOpen
          width={208}
          title={t(translationKeys.CUSTOM_LIST_DELETE_CUSTOM_STATUS_TITLE)}
          text={t(translationKeys.CUSTOM_LIST_DELETE_CUSTOM_STATUS_TEXT)}
          cancelBtnText={t(translationKeys.CANCEL_BUTTON)}
          confirmBtnText={t(translationKeys.DELETE_BUTTON)}
          onCancel={() => setTagToDelete(undefined)}
          onConfirm={() => handleTagDelete(tagToDelete)}
        />
      )}
      {validationErrorKeys && (
        <ConfirmPopup
          id="custom-tags-validation-errors"
          onConfirm={() => setValidationErrorKeys(undefined)}
          onBackdropClick={() => setValidationErrorKeys(undefined)}
          title={t(validationErrorKeys.title)}
          width={208}
          isOpen
          confirmBtnText={t(translationKeys.CUSTOM_LIST_KEEP_EDITING)}
          onCancel={() => setValidationErrorKeys(undefined)}
          text={t(validationErrorKeys.text)}
        />
      )}
      {showUnsavedChangesConfirmPopup && (
        <ConfirmPopup
          id="custom-tags-confirm-exit-popup"
          width={294}
          contentWidth={180}
          isOpen
          title={t(translationKeys.UNSAVED_SETTINGS)}
          text={t(translationKeys.YOU_HAVENOT_SAVED_CHANGES)}
          cancelBtnText={t(translationKeys.CUSTOM_LIST_KEEP_EDITING)}
          confirmBtnText={t(translationKeys.CLOSE_ANYWAY)}
          onCancel={() => setShowUnsavedChangesConfirmPopup(false)}
          onConfirm={() => {
            setShowUnsavedChangesConfirmPopup(false)
            onClose?.()
          }}
        />
      )}
    </ConfirmPopup>
  )
}

export default memo(EditCustomTagsPopover)
